import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  type Connection,
  type Edge,
  type Node,
  type NodeProps,
  Handle,
  Position,
  BackgroundVariant,
  Panel,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Play,
  Trash2,
  Search,
  ChevronRight,
  Terminal,
  Check,
  X,
  Clock,
  Zap,
  Settings,
  GripVertical,
} from 'lucide-react';
import { toast } from 'sonner';
import { nodeCategories, type WorkflowNode, type NodeCategory } from './workflow-nodes-config';

// ─── Types ───────────────────────────────────────────────────────────────────

interface NodeData extends WorkflowNode {
  instanceId: string;
  config: Record<string, unknown>;
  onSelect: (data: NodeData) => void;
  onDelete: (instanceId: string) => void;
  isSelected: boolean;
  [key: string]: unknown;
}

interface ConsoleLog {
  id: string;
  nodeId: string;
  nodeName: string;
  type: 'info' | 'success' | 'error';
  message: string;
  data?: unknown;
  timestamp: Date;
}

interface WorkflowCanvasProps {
  workflowId?: string;
}

// ─── Custom Node Component ────────────────────────────────────────────────────

function WorkflowNodeCard({ data, selected }: NodeProps) {
  const nodeData = data as NodeData;
  const Icon = nodeData.icon as React.ComponentType<{ className?: string }>;

  return (
    <div
      className={`
        bg-card border rounded-xl shadow-md w-[220px] cursor-pointer transition-all
        ${selected ? 'ring-2 ring-primary border-primary shadow-lg' : 'border-border hover:border-primary/50 hover:shadow-lg'}
      `}
      onClick={() => nodeData.onSelect(nodeData)}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-primary border-2 border-background rounded-full"
        style={{ left: -6 }}
      />

      <div className="p-3">
        <div className="flex items-center gap-2">
          <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${nodeData.color} flex-shrink-0`}>
            <Icon className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate leading-tight">{nodeData.name}</p>
            <p className="text-[10px] text-muted-foreground truncate mt-0.5">{nodeData.category}</p>
          </div>
          <button
            className="opacity-0 group-hover:opacity-100 hover:text-destructive transition-opacity p-1 rounded hover:bg-destructive/10"
            onClick={(e) => {
              e.stopPropagation();
              nodeData.onDelete(nodeData.instanceId);
            }}
            title="Eliminar nodo"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>

        {/* Config preview */}
        {Object.keys(nodeData.config || {}).length > 0 && (
          <div className="mt-2 pt-2 border-t border-border">
            <p className="text-[10px] text-muted-foreground truncate">
              {Object.entries(nodeData.config)
                .filter(([, v]) => v)
                .slice(0, 2)
                .map(([k, v]) => `${k}: ${String(v).substring(0, 15)}`)
                .join(' · ')}
            </p>
          </div>
        )}
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-primary border-2 border-background rounded-full"
        style={{ right: -6 }}
      />
    </div>
  );
}

const nodeTypes = { workflowNode: WorkflowNodeCard };

// ─── Main Canvas Component ────────────────────────────────────────────────────

let nodeIdCounter = 1;

export function WorkflowCanvas({ workflowId }: WorkflowCanvasProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selectedNode, setSelectedNode] = useState<NodeData | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [consoleLogs, setConsoleLogs] = useState<ConsoleLog[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['triggers']);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  const filteredCategories = nodeCategories.map(category => ({
    ...category,
    nodes: category.nodes.filter(node =>
      node.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      node.description.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter(category => category.nodes.length > 0);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // ── Callbacks ──

  const handleSelectNode = useCallback((data: NodeData) => {
    setSelectedNode(data);
  }, []);

  const handleDeleteNode = useCallback((instanceId: string) => {
    setNodes(nds => nds.filter(n => n.id !== instanceId));
    setEdges(eds => eds.filter(e => e.source !== instanceId && e.target !== instanceId));
    setSelectedNode(prev => prev?.instanceId === instanceId ? null : prev);
    toast.success('Nodo eliminado');
  }, [setNodes, setEdges]);

  const makeNodeData = useCallback((node: WorkflowNode, instanceId: string, config: Record<string, unknown> = {}): NodeData => ({
    ...node,
    instanceId,
    config,
    onSelect: handleSelectNode,
    onDelete: handleDeleteNode,
    isSelected: false,
  }), [handleSelectNode, handleDeleteNode]);

  // ── Add node from palette ──

  const addNode = useCallback((node: WorkflowNode) => {
    const instanceId = `node_${nodeIdCounter++}`;
    const x = 200 + (nodes.length % 4) * 250;
    const y = 100 + Math.floor(nodes.length / 4) * 160;

    const newNode: Node = {
      id: instanceId,
      type: 'workflowNode',
      position: { x, y },
      data: makeNodeData(node, instanceId),
    };

    setNodes(nds => [...nds, newNode]);
    toast.success(`Nodo "${node.name}" agregado`);
  }, [nodes.length, makeNodeData, setNodes]);

  // ── Drag from palette ──

  const onDragStart = (event: React.DragEvent, node: WorkflowNode) => {
    event.dataTransfer.setData('application/reactflow-node', JSON.stringify(node));
    event.dataTransfer.effectAllowed = 'move';
  };

  const onDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const raw = event.dataTransfer.getData('application/reactflow-node');
    if (!raw) return;

    const node: WorkflowNode = JSON.parse(raw);
    const bounds = reactFlowWrapper.current?.getBoundingClientRect();
    if (!bounds) return;

    const instanceId = `node_${nodeIdCounter++}`;
    const newNode: Node = {
      id: instanceId,
      type: 'workflowNode',
      position: {
        x: event.clientX - bounds.left - 110,
        y: event.clientY - bounds.top - 30,
      },
      data: makeNodeData(node, instanceId),
    };

    setNodes(nds => [...nds, newNode]);
    toast.success(`Nodo "${node.name}" agregado`);
  }, [makeNodeData, setNodes]);

  const onDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  // ── Edges ──

  const onConnect = useCallback((params: Connection) => {
    setEdges(eds => addEdge({
      ...params,
      type: 'smoothstep',
      markerEnd: { type: MarkerType.ArrowClosed, color: 'hsl(var(--primary))' },
      style: { stroke: 'hsl(var(--primary))', strokeWidth: 2 },
      animated: isExecuting,
    }, eds));
  }, [setEdges, isExecuting]);

  // ── Update config ──

  const updateNodeConfig = useCallback((instanceId: string, config: Record<string, unknown>) => {
    setNodes(nds => nds.map(n =>
      n.id === instanceId
        ? { ...n, data: { ...n.data, config } }
        : n
    ));
    setSelectedNode(prev => prev?.instanceId === instanceId ? { ...prev, config } : prev);
  }, [setNodes]);

  // ── Execution ──

  const addConsoleLog = (log: Omit<ConsoleLog, 'id' | 'timestamp'>) => {
    setConsoleLogs(prev => [...prev, { ...log, id: `log_${Date.now()}`, timestamp: new Date() }]);
  };

  const executeWorkflow = async () => {
    if (nodes.length === 0) {
      toast.error('Agrega al menos un nodo al workflow');
      return;
    }

    setIsExecuting(true);
    setConsoleLogs([]);
    setEdges(eds => eds.map(e => ({ ...e, animated: true })));

    addConsoleLog({ nodeId: 'system', nodeName: 'Sistema', type: 'info', message: '🚀 Iniciando ejecución del workflow...' });

    const triggerNodes = nodes.filter(n => (n.data as NodeData).category === 'triggers');
    if (triggerNodes.length === 0) {
      addConsoleLog({ nodeId: 'system', nodeName: 'Sistema', type: 'error', message: '❌ Error: No hay un trigger definido' });
      setIsExecuting(false);
      setEdges(eds => eds.map(e => ({ ...e, animated: false })));
      toast.error('Agrega un nodo trigger para iniciar el workflow');
      return;
    }

    for (const node of nodes) {
      const data = node.data as NodeData;
      await new Promise(resolve => setTimeout(resolve, 700));
      addConsoleLog({ nodeId: node.id, nodeName: data.name, type: 'info', message: `⚡ Ejecutando "${data.name}"...` });
      await new Promise(resolve => setTimeout(resolve, 500));

      const mockResults: Record<string, unknown> = {
        triggers: { trigger: 'activated', timestamp: new Date().toISOString() },
        actions: { status: 'sent', result: 'success' },
        logic: { condition: true, branch: 'true' },
        data: { transformed: true, records: 42 },
        integrations: { connected: true, response: 'OK' },
        ai: { generated: true, tokens: 150, response: 'Respuesta simulada del agente IA.' },
        blockchain: { hash: '0xabc123...', confirmed: true },
        social: { posted: true, postId: '12345' },
      };
      const result = mockResults[data.category] ?? { executed: true };

      addConsoleLog({ nodeId: node.id, nodeName: data.name, type: 'success', message: `✅ "${data.name}" ejecutado`, data: result });
    }

    await new Promise(resolve => setTimeout(resolve, 300));
    addConsoleLog({ nodeId: 'system', nodeName: 'Sistema', type: 'success', message: `🎉 Workflow completado. ${nodes.length} nodos ejecutados.` });

    setIsExecuting(false);
    setEdges(eds => eds.map(e => ({ ...e, animated: false })));
    toast.success('Workflow ejecutado correctamente');
  };

  return (
    <div className="flex h-[calc(100vh-12rem)] gap-4">
      {/* ── Node Palette ── */}
      <div className="w-72 flex-shrink-0 flex flex-col gap-3">
        <Card className="flex-1 relative overflow-hidden border-border flex flex-col">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary" />
          <CardHeader className="pb-2 flex-shrink-0">
            <CardTitle className="text-sm">Paleta de Nodos</CardTitle>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Buscar nodos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-8 text-sm"
              />
            </div>
          </CardHeader>
          <ScrollArea className="flex-1">
            <CardContent className="space-y-1 pb-4">
              {filteredCategories.map((category) => (
                <div key={category.id}>
                  <button
                    className="w-full flex items-center justify-between px-2 py-1.5 rounded-md hover:bg-accent text-sm font-medium transition-colors"
                    onClick={() => toggleCategory(category.id)}
                  >
                    <div className="flex items-center gap-2">
                      <category.icon className={`h-3.5 w-3.5 ${category.color}`} />
                      <span>{category.name}</span>
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                        {category.nodes.length}
                      </Badge>
                    </div>
                    <ChevronRight
                      className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${
                        expandedCategories.includes(category.id) ? 'rotate-90' : ''
                      }`}
                    />
                  </button>

                  {expandedCategories.includes(category.id) && (
                    <div className="mt-1 space-y-0.5 pl-1">
                      {category.nodes.map((node) => (
                        <div
                          key={node.id}
                          className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent cursor-grab active:cursor-grabbing transition-colors group"
                          draggable
                          onDragStart={(e) => onDragStart(e, node)}
                          onClick={() => addNode(node)}
                          title="Arrastra al canvas o haz clic para agregar"
                        >
                          <GripVertical className="h-3 w-3 text-muted-foreground/40 flex-shrink-0" />
                          <div className={`flex h-6 w-6 items-center justify-center rounded-md ${node.color} flex-shrink-0`}>
                            <node.icon className="h-3.5 w-3.5 text-white" />
                          </div>
                          <span className="text-xs font-medium truncate">{node.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </ScrollArea>
        </Card>
      </div>

      {/* ── Canvas + Console ── */}
      <div className="flex-1 flex flex-col gap-3 min-w-0">
        {/* React Flow Canvas */}
        <div className="flex-1 relative rounded-xl overflow-hidden border border-border" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onDragOver={onDragOver}
            nodeTypes={nodeTypes}
            fitView
            snapToGrid
            snapGrid={[16, 16]}
            deleteKeyCode="Delete"
            multiSelectionKeyCode="Shift"
            className="bg-accent/10"
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={20}
              size={1}
              color="hsl(var(--border))"
            />
            <Controls
              className="bg-card border border-border rounded-lg shadow-md"
              showInteractive={false}
            />
            <MiniMap
              className="bg-card border border-border rounded-lg shadow-md"
              nodeColor={(n) => {
                const data = n.data as NodeData;
                const colorMap: Record<string, string> = {
                  'bg-blue-500': '#3b82f6',
                  'bg-green-500': '#22c55e',
                  'bg-purple-500': '#a855f7',
                  'bg-orange-500': '#f97316',
                  'bg-pink-500': '#ec4899',
                  'bg-indigo-500': '#6366f1',
                  'bg-emerald-500': '#10b981',
                  'bg-yellow-500': '#eab308',
                };
                for (const [cls, hex] of Object.entries(colorMap)) {
                  if (data?.color?.includes(cls.replace('bg-', ''))) return hex;
                }
                return 'hsl(var(--primary))';
              }}
              maskColor="hsl(var(--background) / 0.7)"
            />

            {/* Toolbar Panel */}
            <Panel position="top-right" className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {nodes.length} nodos · {edges.length} conexiones
              </Badge>
              <Button
                size="sm"
                onClick={executeWorkflow}
                disabled={isExecuting}
                className="bg-primary hover:bg-primary/90 h-8 text-xs"
              >
                {isExecuting ? (
                  <>
                    <Clock className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    Ejecutando...
                  </>
                ) : (
                  <>
                    <Play className="mr-1.5 h-3.5 w-3.5" />
                    Ejecutar
                  </>
                )}
              </Button>
            </Panel>

            {/* Empty state */}
            {nodes.length === 0 && (
              <Panel position="top-center" className="mt-16 pointer-events-none">
                <div className="text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted mx-auto mb-3">
                    <Zap className="h-7 w-7 text-muted-foreground" />
                  </div>
                  <h3 className="text-base font-medium mb-1">Arrastra nodos aquí</h3>
                  <p className="text-xs text-muted-foreground max-w-[260px]">
                    Arrastra nodos desde la paleta o haz clic en ellos para agregarlos.
                    Conecta los nodos arrastrando desde los puntos laterales.
                  </p>
                </div>
              </Panel>
            )}
          </ReactFlow>
        </div>

        {/* Execution Console */}
        <Card className="h-48 relative overflow-hidden border-border flex flex-col flex-shrink-0">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary" />
          <CardHeader className="border-b border-border py-2 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Terminal className="h-3.5 w-3.5" />
                <CardTitle className="text-sm">Consola</CardTitle>
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                  {consoleLogs.length}
                </Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setConsoleLogs([])} className="h-7 text-xs">
                Limpiar
              </Button>
            </div>
          </CardHeader>
          <ScrollArea className="flex-1">
            <CardContent className="p-2 space-y-1 font-mono text-xs">
              {consoleLogs.length === 0 ? (
                <div className="text-center text-muted-foreground py-6 text-xs">
                  Los logs de ejecución aparecerán aquí
                </div>
              ) : (
                consoleLogs.map((log) => (
                  <div
                    key={log.id}
                    className={`flex items-start gap-1.5 p-1.5 rounded text-xs ${
                      log.type === 'success' ? 'bg-green-500/10 text-green-600 dark:text-green-400' :
                      log.type === 'error' ? 'bg-red-500/10 text-red-600 dark:text-red-400' :
                      'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                    }`}
                  >
                    {log.type === 'success' && <Check className="h-3 w-3 mt-0.5 flex-shrink-0" />}
                    {log.type === 'error' && <X className="h-3 w-3 mt-0.5 flex-shrink-0" />}
                    {log.type === 'info' && <Terminal className="h-3 w-3 mt-0.5 flex-shrink-0" />}
                    <div>
                      <span className="font-bold">[{log.nodeName}]</span>
                      <span className="opacity-60 ml-1">{log.timestamp.toLocaleTimeString()}</span>
                      <span className="ml-1">{log.message}</span>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </ScrollArea>
        </Card>
      </div>

      {/* ── Config Panel ── */}
      {selectedNode && (
        <div className="w-72 flex-shrink-0">
          <Card className="relative overflow-hidden border-border h-full flex flex-col">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary" />
            <CardHeader className="pb-3 flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${selectedNode.color} flex-shrink-0`}>
                  <selectedNode.icon className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-sm truncate">{selectedNode.name}</CardTitle>
                  <p className="text-[10px] text-muted-foreground truncate mt-0.5">{selectedNode.description}</p>
                </div>
                <button
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => setSelectedNode(null)}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </CardHeader>
            <ScrollArea className="flex-1">
              <CardContent className="space-y-3 pb-4">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Settings className="h-3 w-3" />
                  <span>Configuración</span>
                </div>

                {selectedNode.configFields.map((field) => (
                  <div key={field.name} className="space-y-1">
                    <Label htmlFor={field.name} className="text-xs">
                      {field.label}
                      {field.required && <span className="text-destructive ml-1">*</span>}
                    </Label>

                    {(field.type === 'text' || field.type === 'password' || field.type === 'number') && (
                      <Input
                        id={field.name}
                        type={field.type === 'password' ? 'password' : field.type === 'number' ? 'number' : 'text'}
                        placeholder={field.placeholder}
                        value={(selectedNode.config[field.name] as string) || (field as any).defaultValue || ''}
                        onChange={(e) =>
                          updateNodeConfig(selectedNode.instanceId, {
                            ...selectedNode.config,
                            [field.name]: e.target.value,
                          })
                        }
                        className="h-8 text-xs"
                      />
                    )}

                    {(field.type === 'textarea' || field.type === 'json' || field.type === 'code') && (
                      <textarea
                        id={field.name}
                        placeholder={field.placeholder}
                        value={(selectedNode.config[field.name] as string) || (field as any).defaultValue || ''}
                        onChange={(e) =>
                          updateNodeConfig(selectedNode.instanceId, {
                            ...selectedNode.config,
                            [field.name]: e.target.value,
                          })
                        }
                        rows={field.type === 'code' || field.type === 'json' ? 5 : 3}
                        className={`flex w-full rounded-md border border-input bg-background px-3 py-2 text-xs font-${field.type === 'code' || field.type === 'json' ? 'mono' : 'sans'} ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none`}
                      />
                    )}

                    {field.type === 'select' && field.options && (
                      <select
                        id={field.name}
                        value={(selectedNode.config[field.name] as string) || (field as any).defaultValue || ''}
                        onChange={(e) =>
                          updateNodeConfig(selectedNode.instanceId, {
                            ...selectedNode.config,
                            [field.name]: e.target.value,
                          })
                        }
                        className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <option value="">Seleccionar...</option>
                        {field.options.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    )}

                    {field.type === 'boolean' && (
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={field.name}
                          checked={Boolean(selectedNode.config[field.name] ?? (field as any).defaultValue)}
                          onChange={(e) =>
                            updateNodeConfig(selectedNode.instanceId, {
                              ...selectedNode.config,
                              [field.name]: e.target.checked,
                            })
                          }
                          className="rounded"
                        />
                        <Label htmlFor={field.name} className="text-xs font-normal cursor-pointer">
                          {field.label}
                        </Label>
                      </div>
                    )}

                    {field.description && (
                      <p className="text-[10px] text-muted-foreground leading-relaxed">{field.description}</p>
                    )}
                  </div>
                ))}
              </CardContent>
            </ScrollArea>
          </Card>
        </div>
      )}
    </div>
  );
}
